import * as React from "react";

import { UitkToolbar } from "@egds/react-core/toolbar";
import { UitkSecondaryButton } from "@egds/react-core/button";
import { UitkSheet, UitkSheetContent } from "@egds/react-core/sheet";
import { UitkHeading, UitkText } from "@egds/react-core/text";
import { UitkCard, UitkCardContentSection } from "@egds/react-core/cards";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";

import { withStores } from "stores";
import { formatTimeSincePosted } from "../DestinationUserReviewsUtils";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { UserReview } from "typings/microserviceModels/destination-user-reviews-flex-module";
import { DestinationUserReviewsDialogProps } from "../typings";
import { useLocalization } from "@shared-ui/localization-context";

const BLOCK_OF_VISIBLE_REVIEWS = 20;

const DestinationUserReviewsDialog = withStores("analytics")((props: DestinationUserReviewsDialogProps) => {
  const { nameForTracking, reviewDialogTitle, reviews, toggleAllDestinationReviews, isOpen } = props;

  const [maxVisibleReviews, setMaxVisibleReviews] = React.useState(BLOCK_OF_VISIBLE_REVIEWS);
  const triggerRef = React.createRef<HTMLElement>();

  const showMoreReviews = () => setMaxVisibleReviews(maxVisibleReviews + BLOCK_OF_VISIBLE_REVIEWS);

  const analytics = {
    id: "DestinationUserReviewsClose",
    callback: (theId: string) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: theId,
        action: Action.CLICK,
      };

      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const ButtonTracked = FlexClickTracker(UitkSecondaryButton);
  const { formatText } = useLocalization();

  const reviewsToShow = reviews.slice(0, maxVisibleReviews).map((review: UserReview, index: number) => (
    <UitkSpacing margin={{ blockend: "four" }} key={review.postStayReviewId}>
      <UitkCard>
        <UitkCardContentSection border="bottom" data-testid={`comment-review-${index}`}>
          <UitkSpacing padding={{ block: "two", inlinestart: "three" }} margin={{ block: "two", inlinestart: "two" }}>
            <div className="CommentStyleReview">
              {review.rating && (
                <UitkSpacing margin={{ blockend: "two" }}>
                  <UitkHeading tag="h3" size={6}>
                    {`${review.rating}/5 - ${formatText(`destinationReviews.descriptor.${review.rating}`)}`}
                  </UitkHeading>
                </UitkSpacing>
              )}

              <UitkText size={200}>{review.review}</UitkText>
              <UitkSpacing margin={{ blockstart: "two" }}>
                <div>
                  <UitkText size={200} weight="bold" inline>
                    {review.reviewerDisplayName || formatText("destinationReviews.verified.traveler")}
                  </UitkText>
                  <UitkText size={200} inline>{`, ${formatTimeSincePosted(review.createTime)}`}</UitkText>
                </div>
              </UitkSpacing>
            </div>
          </UitkSpacing>
        </UitkCardContentSection>
      </UitkCard>
    </UitkSpacing>
  ));

  return (
    <UitkSheet type="centered" isVisible={isOpen} triggerRef={triggerRef} centeredSheetSize="small">
      <UitkToolbar
        toolbarTitle={reviewDialogTitle}
        navigationContent={{
          navIconLabel: "close reviews",
          onClick: toggleAllDestinationReviews,
          onClickAnalytics: analytics,
        }}
      />

      <UitkSheetContent>
        {reviewsToShow}

        {reviews.length > maxVisibleReviews && (
          <UitkLayoutFlex justifyContent="center">
            <UitkLayoutFlexItem>
              <ButtonTracked
                onClick={showMoreReviews}
                rfrr="Dialog.ShowMore"
                moduleName={nameForTracking}
                linkName="RFRR Action Event"
              >
                {formatText("destinationReviews.showMore")}
              </ButtonTracked>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </UitkSheetContent>
    </UitkSheet>
  );
});

export default DestinationUserReviewsDialog;
